// HeaderSection.js — Header personalizable para landing
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useIdioma } from '../../../contexts/IdiomaContext';
import { buildBg } from '../bgHelpers';
import CustomMenu from '../CustomMenu';
import PaisSelector from '../PaisSelector';
import { resolveDeviceValue } from '../../admin/DeviceTabsSelect';
import AccesoIconoModal from '../AccesoIconoModal';
import BuscadorCursos from '../BuscadorCursos';
import CampanaAlertas from '../../alertas/CampanaAlertas';
import { EnlacesSesion, BotonUsuario } from '../MenuUsuario';

const Wrapper = styled.header`
  width: 100%;
  background: ${p => p.$bg || '#fff'};
  /* Imagen de fondo opcional (p. ej. una forma decorativa que se prolonga hacia
     el hero). Va como capa aparte para no pisar el color/degradado de arriba. */
  ${p => p.$bgImage && `
    background-image: url(${p.$bgImage});
    background-size: ${p.$bgSize || 'auto'};
    background-position: ${p.$bgPos || 'left top'};
    background-repeat: ${p.$bgRepeat || 'no-repeat'};
    background-attachment: ${p.$bgAttach || 'scroll'};
  `}
  ${p => p.$bgImage && p.$bgOcultarMovil ? '@media (max-width: 991px) { background-image: none; }' : ''}
  box-shadow: ${p => p.$shadow || '0 2px 8px rgba(0,0,0,0.06)'};
  position: ${p => p.$fixed ? 'fixed' : 'relative'};
  left: 0;
  ${p => p.$fixed
    ? (p.$pos === 'abajo' ? 'bottom: 0;' : 'top: 0;')
    : 'top: 0;'}
  z-index: 900;
  transition: background 0.3s ease, box-shadow 0.3s ease;
  /* En móvil, el header fijo con fondo sólido se vuelve relativo para no tapar
     el hero. El transparente (overlay) flota SOBRE el banner igual que en
     escritorio → conserva position: fixed también en móvil/tablet. */
  @media (max-width: 768px) {
    ${p => p.$transparent ? '' : 'position: relative; top: auto; bottom: auto;'}
  }
`;

/* Forma decorativa (SVG/PNG) anclada a una esquina del header. Puede ser más
   alta que el propio header: al desbordar, se prolonga sobre el banner de
   abajo, que es como el diseño encadena la cuña lateral. No captura clics. */
const FormaDecor = styled.div`
  position: absolute;
  top: 0;
  ${p => (p.$lado === 'derecha' ? 'right: 0;' : 'left: 0;')}
  width: ${p => p.$w || '24%'};
  height: ${p => p.$h || '100%'};
  background-image: url(${p => p.$url});
  background-size: 100% 100%;
  background-repeat: no-repeat;
  opacity: ${p => (p.$opacity != null ? p.$opacity : 1)};
  pointer-events: none;
  z-index: 0;
  @media (max-width: 991px) { ${p => p.$hideCompact && 'display: none;'} }
`;

/* Barra superior (topbar): va en la MISMA fila que el logo, apilada sobre el
   menú. El logo queda a la izquierda, centrado verticalmente abarcando las dos
   filas (barra arriba, menú abajo), todo dentro del mismo contenedor. */
const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$align === 'izquierda' ? 'flex-start' : p.$align === 'centro' ? 'center' : 'flex-end'};
  gap: ${p => p.$gap || '0.6rem'};
  color: ${p => p.$color || 'inherit'};
  font-size: ${p => p.$size || '0.8rem'};
`;

const RightColTop = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  @media (max-width: 600px) {
    ${p => p.$ocultarMovil ? 'display: none;' : ''}
  }
`;

const TopBarItem = styled.a`
  color: inherit;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: ${p => p.$link ? 'pointer' : 'default'};
  &:hover { ${p => p.$link ? 'text-decoration: underline; opacity: 0.85;' : ''} }
`;

/* Helper para traducir 'left' | 'center' | 'right' a flex-direction. */
const dirFor = (align) => {
  if (align === 'center') return 'column';
  if (align === 'right') return 'row-reverse';
  return 'row';
};
const alignItemsFor = (align) => (align === 'center' ? 'center' : 'center');

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1200px'};
  width: ${p => p.$ancho || 'auto'};
  /* El padding va dentro del ancho: con ancho 100% la barra medía de más y
     dejaba scroll horizontal en la página. */
  box-sizing: border-box;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: ${p => alignItemsFor(p.$logoDesktop)};
  justify-content: ${p => p.$center ? 'center' : (p.$agrupar ? 'flex-start' : 'space-between')};
  flex-direction: ${p => dirFor(p.$logoDesktop)};
  gap: ${p => p.$logoDesktop === 'center' ? '0.5rem' : (p.$agrupar || '0')};
  padding: ${p => p.$padding || '0.75rem 2rem'};
  /* Barra flotante: la píldora blanca que se apoya sobre el banner. El
     contenedor exterior queda transparente y el color/sombra viven acá. */
  ${p => p.$barra && `
    background: ${p.$barraBg || '#fff'};
    border-radius: ${p.$barraRadio || '0 0 40px 40px'};
    box-shadow: ${p.$barraSombra || '0 4px 18px rgba(15,23,42,0.10)'};
  `}
  @media (max-width: 991px) and (min-width: 601px) {
    ${p => p.$barra && p.$anchoTablet ? `width: ${p.$anchoTablet};` : ''}
    /* Logo y menú juntos solo en escritorio: con el menú ya en hamburguesa
       vuelven a los extremos. */
    ${p => p.$agrupar && !p.$center ? 'justify-content: space-between; gap: 0;' : ''}
    flex-direction: ${p => dirFor(p.$logoTablet || p.$logoDesktop)};
    align-items: ${p => alignItemsFor(p.$logoTablet || p.$logoDesktop)};
    gap: ${p => (p.$logoTablet || p.$logoDesktop) === 'center' ? '0.5rem' : '0'};
  }
  @media (max-width: 600px) {
    padding: 0.5rem 1rem;
    ${p => p.$agrupar && !p.$center ? 'justify-content: space-between; gap: 0;' : ''}
    ${p => p.$barra && p.$anchoMobile ? `width: ${p.$anchoMobile};` : ''}
    ${p => p.$barra && `border-radius: ${p.$barraRadioMovil || '0 0 22px 22px'};`}
    flex-direction: ${p => dirFor(p.$logoMobile || p.$logoTablet || p.$logoDesktop)};
    align-items: ${p => alignItemsFor(p.$logoMobile || p.$logoTablet || p.$logoDesktop)};
    gap: ${p => (p.$logoMobile || p.$logoTablet || p.$logoDesktop) === 'center' ? '0.5rem' : '0'};
  }
`;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  height: ${p => p.$h || '48px'};
  width: ${p => p.$w || 'auto'};
  object-fit: contain;
  ${p => p.$wTablet ? `@media (max-width: 991px) and (min-width: 601px) { width: ${p.$wTablet}; }` : ''}
  ${p => p.$wMobile ? `@media (max-width: 600px) { width: ${p.$wMobile}; }` : ''}
`;

const BrandText = styled.span`
  font-size: ${p => p.$size || '1.3rem'};
  font-weight: ${p => p.$weight || '700'};
  color: ${p => p.$color || '#0f355b'};
  font-family: inherit;
`;

const NavArea = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 768px) { gap: 0.3rem; }
`;

const NavBtn = styled.button`
  background: ${p => p.$bg || 'transparent'};
  color: ${p => p.$color || '#0f355b'};
  border: ${p => p.$border || '1px solid transparent'};
  ${p => p.$borderLeft && `border-left: ${p.$borderLeft};`}
  ${p => p.$borderRight && `border-right: ${p.$borderRight};`}
  padding: ${p => p.$padding || '0.45rem 1.2rem'};
  border-radius: ${p => p.$radius || '8px'};
  font-weight: ${p => p.$weight || '600'};
  font-size: ${p => p.$size || '0.9rem'};
  cursor: pointer;
  transition: 0.2s;
  font-family: inherit;
  white-space: nowrap;
  &:hover { opacity: 0.85; transform: translateY(-1px); }
`;

const NavLink = styled(Link)`
  color: ${p => p.$color || '#0f355b'};
  text-decoration: none;
  font-weight: ${p => p.$weight || '600'};
  font-size: ${p => p.$size || '0.9rem'};
  padding: 0.45rem 0.9rem;
  border-radius: 8px;
  &:hover { background: rgba(0,0,0,0.05); }
`;

const Avatar = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px; height: 36px;
  border-radius: 50%;
  background: ${p => p.$bg || '#0f355b'};
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  margin-left: 0.4rem;
  overflow: hidden;
  &:hover { opacity: 0.9; }
`;

// Con `login_icono`, «Buscar» e «Iniciar sesión» son dos iconos dentro de una
// cápsula de vidrio (como la barra de herramientas de una app), a la misma
// altura que «Olvidé mi contraseña». Al pasar, el círculo se llena de blanco y
// aparece una etiqueta flotante debajo. La tecla «/» también abre el buscador.
const GrupoIconos = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 50px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(8, 10, 14, 0.4);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, ${p => p.$color} 22%, transparent), 0 8px 24px -14px rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  .sep { width: 1px; height: 22px; margin: 0 2px; background: color-mix(in srgb, ${p => p.$color} 25%, transparent); }
`;

const IconoBoton = styled.button`
  position: relative;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${p => p.$color};
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
  svg { width: 24px; height: 24px; transition: transform 0.25s cubic-bezier(0.3, 1.5, 0.5, 1); }
  &:hover, &:focus-visible { background: #fff; color: #0a0c10; }
  &:hover svg { transform: scale(1.08); }
  &:active { transform: scale(0.92); }
  &:focus-visible { outline: 2px solid ${p => p.$acento}; outline-offset: 3px; }
  /* Etiqueta flotante debajo del icono. */
  &::after {
    content: attr(data-tip);
    position: absolute;
    top: calc(100% + 12px);
    left: 50%;
    transform: translate(-50%, -4px);
    padding: 0.38rem 0.62rem;
    border-radius: 6px;
    background: rgba(12, 14, 18, 0.94);
    box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.7), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 0.74rem;
    font-weight: 600;
    white-space: pre;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.2s ease;
  }
  &:hover::after, &:focus-visible::after { opacity: 1; transform: translate(-50%, 0); }
  @media (hover: none) { &::after { display: none; } }
  @media (prefers-reduced-motion: reduce) { transition: none; svg, &::after { transition: none; } }
`;

const IcoPersona = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20.5c.9-3.6 3.9-6 7.5-6s6.6 2.4 7.5 6" />
  </svg>
);

const IcoLupa = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6.75" />
    <path d="m15.6 15.6 5.4 5.4" />
  </svg>
);

// «Olvidé mi contraseña» destacado (con `login_icono`): píldora de vidrio como la
// cápsula de los iconos, con el candado en un círculo del color de acento. Al
// pasar, el candado se abre (el arco sube y gira) y un borde de luz recorre la
// píldora. En el celular queda solo el candado, para no apretar el logo.
const girarBorde = keyframes`from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); }`;
const abrir = keyframes`
  0% { transform: translateY(0) rotate(0); }
  45% { transform: translateY(-2.6px) rotate(0); }
  100% { transform: translateY(-2.6px) rotate(-28deg); }
`;
const BotonOlvido = styled.button`
  --acento-o: ${p => p.$bg};
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  gap: 11px;
  height: 50px;
  padding: 0 1.25rem 0 5px;
  border: 0;
  border-radius: 999px;
  background: rgba(10, 10, 10, 0.55);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14), 0 10px 30px -14px rgba(0, 0, 0, 0.8);
  color: #fff;
  font-family: inherit;
  font-size: ${p => p.$size || '0.95rem'};
  font-weight: 650;
  letter-spacing: -0.005em;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.25s ease, background 0.25s ease;
  overflow: hidden;
  /* Borde de luz que gira al pasar: un degradado cónico grande que rota detrás
     de una capa del color de la píldora, de modo que solo se ve en el borde. */
  &::before {
    content: '';
    position: absolute;
    z-index: -2;
    top: 50%;
    left: 50%;
    width: 260%;
    aspect-ratio: 1;
    background: conic-gradient(transparent 0 58%, var(--acento-o) 74%, #fff 80%, transparent 90%);
    transform: translate(-50%, -50%) rotate(0deg);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 1.5px;
    border-radius: inherit;
    background: rgba(16, 16, 16, 0.9);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  .pozo {
    position: relative;
    display: grid;
    place-items: center;
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, color-mix(in srgb, var(--acento-o) 70%, #fff), var(--acento-o) 60%);
    box-shadow: 0 6px 18px -6px var(--acento-o), inset 0 1px 0 rgba(255, 255, 255, 0.35);
    color: ${p => p.$color};
    transition: transform 0.3s cubic-bezier(0.3, 1.6, 0.5, 1);
  }
  svg { width: 20px; height: 20px; overflow: visible; }
  .arco { transform-origin: 16px 10.5px; transform-box: view-box; }
  .texto { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.1; }
  .texto small { font-size: 0.68em; font-weight: 500; color: rgba(255, 255, 255, 0.62); letter-spacing: 0.01em; }
  &:hover, &:focus-visible {
    transform: translateY(-1px);
    background: rgba(18, 18, 18, 0.72);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06), 0 14px 34px -14px var(--acento-o);
  }
  &:hover::before, &:focus-visible::before { opacity: 1; animation: ${girarBorde} 2.6s linear infinite; }
  &:hover::after, &:focus-visible::after { opacity: 1; }
  &:hover .pozo, &:focus-visible .pozo { transform: scale(1.06) rotate(-6deg); }
  &:hover .arco, &:focus-visible .arco { animation: ${abrir} 0.45s ease-out forwards; }
  &:active { transform: scale(0.97); }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
    padding: 0;
    justify-content: center;
    .texto { display: none; }
    .pozo { width: 36px; height: 36px; }
    svg { width: 18px; height: 18px; }
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover::before, &:focus-visible::before { animation: none; }
    .pozo { transition: none; }
    &:hover .arco, &:focus-visible .arco { animation: none; }
  }
`;
const IcoCandado = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path className="arco" d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    <rect x="4.8" y="10.5" width="14.4" height="10" rx="2.6" fill="currentColor" fillOpacity="0.18" />
    <path d="M12 14.6v2" />
    <circle cx="12" cy="14.3" r=".6" fill="currentColor" />
  </svg>
);

const LangGroup = styled.div`
  display: inline-flex;
  margin-left: 0.4rem;
  border-radius: 999px;
  background: rgba(0,0,0,0.06);
  padding: 2px;
`;

const LangBtn = styled.button`
  background: ${p => p.$active ? '#fff' : 'transparent'};
  color: ${p => p.$active ? (p.$accent || '#0f355b') : '#666'};
  border: none;
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 999px;
  cursor: pointer;
  ${p => p.$active && 'box-shadow: 0 1px 3px rgba(0,0,0,0.15);'}
`;

const HeaderSection = ({ config, proyecto, openLogin, openRegister, tl }) => {
  const c = config;
  const primary = c.color_primario || proyecto?.color_primario || '#0f355b';
  const secondary = c.color_secundario || proyecto?.color_secundario || '#14b6cb';
  const navColor = c.color_texto_botones || primary;

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { idioma, setIdioma, t } = useIdioma();

  // ¿Estamos en la página principal (landing)?
  const esLanding = location.pathname === '/' || location.pathname === '';
  // El admin puede pedir desactivar la opción "fijo" específicamente en la
  // landing — útil cuando el hero ocupa toda la pantalla y el header sticky
  // se vería raro arriba.
  const fijoEfectivo = c.fijo && !(esLanding && c.desactivar_fijo_en_landing === true);

  // Fondo que aparece al hacer scroll (navbar transparente arriba → sólido al bajar).
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    if (!fijoEfectivo || !c.activar_scroll_color) { setIsScrolled(false); return; }
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fijoEfectivo, c.activar_scroll_color]);
  const scrollActivo = isScrolled && c.activar_scroll_color;

  // Logo del header. `ocultar_logo` permite un header solo-menú (el logo del
  // proyecto no se usa como fallback en ese caso).
  const logoUrl = c.ocultar_logo === true
    ? null
    : (c.logo
      ? (c.logo.startsWith('http') ? c.logo : `${BACKEND_BASE}/${c.logo}`)
      : (proyecto?.logo ? `${BACKEND_BASE}/${proyecto.logo}` : null));

  const formaUrl = c.forma
    ? (c.forma.startsWith('http') ? c.forma : `${BACKEND_BASE}/${c.forma}`)
    : null;

  const fondoImg = c.imagen_fondo
    ? (c.imagen_fondo.startsWith('http') ? c.imagen_fondo : `${BACKEND_BASE}/${c.imagen_fondo}`)
    : null;

  // Con `login_icono`, el acceso usa su propio modal en vez del general.
  const [accesoAbierto, setAccesoAbierto] = useState(false);
  const [vistaAcceso, setVistaAcceso] = useState('entrar');
  // Con acceso en página propia (auth_config.login_pagina) no hay modal: se navega a /login.
  const accesoEnPagina = !!proyecto?.config_json?.auth_config?.login_pagina;
  const [buscadorAbierto, setBuscadorAbierto] = useState(false);
  const lupa = c.login_icono === true && c.buscador_cursos !== false ? (
    <IconoBoton
      type="button"
      $color={navColor}
      $acento={c.color_boton_registro || secondary}
      onClick={() => setBuscadorAbierto(true)}
      aria-label="Buscar un curso (atajo: tecla /)"
      aria-keyshortcuts="/"
      data-tip={`${tl(c.texto_buscador, 'Buscar')}   /`}
    >
      <IcoLupa />
    </IconoBoton>
  ) : null;
  // Tecla «/» abre el buscador (si no se está escribiendo en otro campo).
  const conBuscador = !!lupa;
  useEffect(() => {
    if (!conBuscador) return undefined;
    const tecla = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      setBuscadorAbierto(true);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [conBuscador]);

  const handleAction = (action) => {
    if (action === 'login' && c.login_icono === true && accesoEnPagina) navigate('/login');
    else if (action === 'login' && c.login_icono === true) { setVistaAcceso('entrar'); setAccesoAbierto(true); }
    else if (action === 'login') openLogin?.();
    else if (action === 'registro') openRegister?.();
    else if (action && action.startsWith('http')) window.open(action, '_blank');
  };

  const handleLogout = () => {
    logout();
    // Redirección post-logout configurable por proyecto: si hay una URL externa
    // (config_json.logout_redirect_url), se va allí; si no, al login interno.
    const rd = proyecto?.config_json?.auth_config?.logout_destino || proyecto?.config_json?.logout_redirect_url;
    if (rd && /^https?:\/\//i.test(rd)) window.location.href = rd;
    else if (rd && rd.startsWith('/')) navigate(rd);   // p. ej. «/» en INS: la portada, sin el modal de acceso
    else navigate('/login');
  };

  // Iniciales para el avatar (cuando el usuario no tiene avatar_url)
  const iniciales = user
    ? (user.nombre || user.email || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '';

  // Idiomas del proyecto. AdminProyectos los guarda en `idiomas_disponibles`
  // (se mantiene `idiomas_habilitados` como fallback por compatibilidad).
  // El selector SOLO se muestra si hay 2 o más idiomas configurados.
  const idiomasCfg = proyecto?.config_json?.idiomas_disponibles
    ?? proyecto?.config_json?.idiomas_habilitados;
  const idiomasHabilitados = (Array.isArray(idiomasCfg) ? idiomasCfg : []).map(l => l.toLowerCase());
  const mostrarSelectorIdioma = idiomasHabilitados.length >= 2;

  // Menú personalizado del proyecto (config_json.menu_config). Si tiene ítems,
  // reemplaza la navegación por defecto del header.
  const menuCfg = proyecto?.config_json?.menu_config;
  const menuItems = Array.isArray(menuCfg?.items) ? menuCfg.items : [];
  // Mostrar/ocultar el menú (NavArea) y su posición respecto al logo.
  const mostrarMenu = c.mostrar_menu !== false;
  const menuOrder = c.posicion_menu === 'izquierda' ? -1 : 0;

  // ¿El header tiene algo que mostrar para este visitante? Con menú propio, los
  // ítems pueden ser todos "solo con sesión": al cerrar sesión quedaría una
  // barra vacía ocupando alto. En ese caso no se pinta el contenido (la forma
  // decorativa, si la hay, sí se conserva).
  const menuItemsVisibles = menuItems.filter(it => {
    if (it.solo_logueado && !user) return false;
    if (it.solo_invitado && user) return false;
    if (it.tipo === 'pagina' && !it.pagina_slug) return false;
    return true;
  });

  // Barra flotante: el header ocupa todo el ancho pero el color vive en una
  // píldora centrada que se apoya sobre el banner de abajo. Al activarla el
  // contenedor exterior queda transparente aunque no se marque "fondo
  // transparente", que es lo que hace que la píldora se vea flotar.
  const barraFlotante = c.barra_flotante === true;
  const barraAncho = resolveDeviceValue(c.barra_ancho, '');

  // Selector de país del visitante (catálogo en config_json.paises_config).
  // Por defecto solo se ofrece a quien no ha iniciado sesión: con sesión, el
  // país es el de la cuenta.
  const mostrarSelectorPais = c.selector_paises === true
    && (c.selector_paises_logueado === true || !user);

  const selectorPais = mostrarSelectorPais ? (
    <PaisSelector
      colorBorde={c.selector_paises_borde}
      colorBordeAncho={c.selector_paises_borde_ancho}
      colorBordeHover={c.selector_paises_borde_hover}
      colorTextoHover={c.selector_paises_color_hover}
      colorTexto={c.selector_paises_color}
      colorFondo={c.selector_paises_fondo}
      acento={c.selector_paises_acento || secondary}
      tamano={c.selector_paises_tamano}
      mostrarReset={c.selector_paises_reset !== false}
    />
  ) : null;

  // Barra superior opcional (encima del menú).
  const topItems = Array.isArray(c.topbar_items) ? c.topbar_items.filter(i => i && (i.texto || i.icono)) : [];
  const mostrarTopbar = c.topbar_activo === true && topItems.length > 0;

  const renderTopItems = () => topItems.map((it, i) => {
    const iconUrl = it.icono
      ? (it.icono.startsWith('http') ? it.icono : `${BACKEND_BASE}/${it.icono}`)
      : null;
    const esLink = !!it.url;
    const externo = esLink && /^(https?:\/\/|mailto:|tel:)/i.test(it.url);
    return (
      <TopBarItem
        key={it.id || i}
        $link={esLink}
        as={esLink && !externo ? Link : 'a'}
        {...(esLink
          ? (externo
              ? { href: it.url, target: it.nueva_pestana ? '_blank' : undefined, rel: 'noopener noreferrer' }
              : { to: it.url })
          : {})}
      >
        {iconUrl && <img src={iconUrl} alt={it.texto || ''} style={{ height: it.icono_alto || '28px', width: 'auto', display: 'block' }} />}
        {it.texto && <span>{tl(it.texto)}</span>}
      </TopBarItem>
    );
  });

  // Con menú personalizado, la navegación por defecto no se pinta: si no queda
  // ningún ítem visible y tampoco hay logo, marca ni topbar, el header no tiene
  // contenido. Sin menú propio siempre hay botones (login/registro o sesión).
  const hayContenido = !!logoUrl
    || !!c.texto_marca
    || mostrarTopbar
    || !!selectorPais
    || (mostrarMenu && (menuItems.length === 0 || menuItemsVisibles.length > 0 || (user && !menuCfg?.ocultar_perfil)));


  return (
    <Wrapper
      $bg={barraFlotante && !scrollActivo ? 'transparent' : (scrollActivo ? (c.color_fondo_scroll || '#fff') : buildBg(c, '#fff'))}
      $bgImage={fondoImg}
      $bgSize={c.tamano_fondo}
      $bgPos={c.posicion_fondo}
      $bgRepeat={c.repeat_fondo}
      $bgAttach={c.attach_fondo}
      $bgOcultarMovil={c.ocultar_imagen_fondo_movil === true}
      $shadow={barraFlotante ? 'none' : (scrollActivo ? (c.sombra_scroll || c.sombra) : c.sombra)}
      $fixed={fijoEfectivo}
      $transparent={!!c.fondo_transparente || barraFlotante}
      $pos={c.posicion_fija}
    >
      {formaUrl && (
        <FormaDecor
          $url={formaUrl}
          $w={c.forma_ancho}
          $h={c.forma_alto}
          $lado={c.forma_lado}
          $opacity={c.forma_opacidad != null ? parseFloat(c.forma_opacidad) : 1}
          $hideCompact={c.forma_ocultar_movil !== false}
        />
      )}
      {hayContenido && (
      <Inner
        $padding={c.padding}
        $barra={barraFlotante}
        $barraBg={c.barra_fondo}
        $barraRadio={c.barra_radio}
        $barraRadioMovil={c.barra_radio_movil}
        $barraSombra={c.barra_sombra}
        $ancho={barraAncho.desktop || undefined}
        $anchoTablet={barraAncho.tablet || undefined}
        $anchoMobile={barraAncho.mobile || undefined}
        $maxWidth={c.ancho_maximo}
        $center={c.centrar_contenido === true}
        /* Logo y menú juntos en el lado del logo (p. ej. menú pegado al logo a la
           derecha) en vez de repartidos a los extremos. El valor es la separación. */
        $agrupar={c.agrupar_logo_menu ? (typeof c.agrupar_logo_menu === 'string' ? c.agrupar_logo_menu : '2rem') : undefined}
        $logoDesktop={(c.alineacion_logo && (typeof c.alineacion_logo === 'object' ? c.alineacion_logo.desktop : c.alineacion_logo)) || 'left'}
        $logoTablet={c.alineacion_logo && typeof c.alineacion_logo === 'object' ? c.alineacion_logo.tablet : null}
        $logoMobile={c.alineacion_logo && typeof c.alineacion_logo === 'object' ? c.alineacion_logo.mobile : null}
      >
        <LogoArea>
          {logoUrl && (() => {
            const lw = resolveDeviceValue(c.ancho_logo, '');
            return <Link to="/"><Logo src={logoUrl} alt="" $h={c.altura_logo || '48px'} $w={lw.desktop || undefined} $wTablet={lw.tablet || undefined} $wMobile={lw.mobile || undefined} /></Link>;
          })()}
          {c.texto_marca && !(c.ocultar_marca_invitado === true && !user) && (
            <BrandText $color={c.color_texto || primary} $size={c.tamano_marca} $weight={c.peso_marca}>
              {tl(c.texto_marca)}
            </BrandText>
          )}
          {/* Con sesión (modo login_icono): accesos junto al logo, p. ej. Progreso y Mis certificados. */}
          {user && c.login_icono === true && c.mostrar_enlaces_sesion !== false && (
            <EnlacesSesion enlaces={c.enlaces_sesion} color={navColor} acento={c.color_boton_registro || secondary} />
          )}
        </LogoArea>
        {mostrarMenu && (() => {
        const nav = (
        <NavArea>
          {menuItems.length > 0 ? (
            // ===== MENÚ PERSONALIZADO (config_json.menu_config) =====
            <>
              <CustomMenu
                items={menuItems}
                posicion={menuCfg.posicion || 'derecha'}
                fontSize={menuCfg.font_size}
                pesoMenu={c.peso_menu || menuCfg.font_weight || undefined}
                movilBg={menuCfg.movil_fondo}
                movilAncho={menuCfg.movil_ancho}
                movilFontSize={menuCfg.movil_font_size}
                movilColor={menuCfg.movil_color_texto}
                subBg={menuCfg.submenu_fondo}
                subColor={menuCfg.submenu_color}
                hoverColor={c.color_menu_hover || menuCfg?.color_hover || undefined}
                hoverBg={c.fondo_menu_hover || undefined}
                activoBg={menuCfg.fondo_activo || undefined}
                activoColor={menuCfg.color_activo || undefined}
                colorOverride={c.color_menu || undefined}
                itemColors={c.menu_colores || undefined}
                bgMenu={c.fondo_menu || undefined}
                radiusMenu={c.radio_menu || undefined}
                padMenu={c.padding_menu || undefined}
                sepMenu={c.separador_menu || undefined}
                fontMenu={c.fuente_menu || undefined}
                user={user}
                accent={navColor}
                onLogin={openLogin}
                onRegister={openRegister}
                onLogout={handleLogout}
              />
              {user && !menuCfg?.ocultar_perfil && (
                <Avatar to="/perfil" title="Mi perfil" $bg={primary}>
                  {user.avatar_url
                    ? <img src={`/backend/${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : iniciales}
                </Avatar>
              )}
              {mostrarSelectorIdioma && (
                <LangGroup>
                  {idiomasHabilitados.map(lang => (
                    <LangBtn key={lang} $active={idioma === lang} $accent={primary} onClick={() => setIdioma(lang)}>
                      {lang.toUpperCase()}
                    </LangBtn>
                  ))}
                </LangGroup>
              )}
            </>
          ) : user ? (
            // ============== USUARIO LOGUEADO ==============
            c.login_icono === true ? (
              // Cápsula de vidrio: lupa + usuario con su menú (perfil, tablero, cursos, certificados, salir).
              <>
                <GrupoIconos $color={navColor}>
                  {lupa}
                  {c.alertas !== false && proyecto?.config_json?.alertas?.activo === true && <CampanaAlertas proyectoId={proyecto?.id} color={navColor} acento={c.color_boton_registro || secondary} Boton={IconoBoton} />}
                  <span className="sep" aria-hidden="true" />
                  <BotonUsuario
                    user={user}
                    items={c.menu_usuario}
                    color={navColor}
                    acento={c.color_boton_registro || secondary}
                    proyecto={proyecto}
                    onLogout={handleLogout}
                    textoProgreso={c.texto_progreso_menu}
                  />
                </GrupoIconos>
                {mostrarSelectorIdioma && (
                  <LangGroup>
                    {idiomasHabilitados.map(lang => (
                      <LangBtn key={lang} $active={idioma === lang} $accent={primary} onClick={() => setIdioma(lang)}>
                        {lang.toUpperCase()}
                      </LangBtn>
                    ))}
                  </LangGroup>
                )}
              </>
            ) : (
            <>
              {lupa}
              {c.alertas !== false && proyecto?.id && proyecto?.config_json?.alertas?.activo === true && <CampanaAlertas proyectoId={proyecto.id} color={navColor} acento={secondary} />}
              {user.role === 'admin' && (
                <NavLink to="/admin-panel" $color={navColor}>{t('panel_admin') || 'Panel Admin'}</NavLink>
              )}
              <NavLink to="/contenidos" $color={navColor}>{t('contenidos') || 'Contenidos'}</NavLink>
              <NavBtn
                $bg="transparent"
                $color={navColor}
                $border={c.borde_boton_login || '1px solid transparent'}
                $padding={c.padding_botones}
                $radius={c.radio_botones}
                $weight={c.peso_login}
                $size={c.tamano_botones}
                onClick={handleLogout}
              >
                {t('cerrar_sesion') || 'Cerrar Sesión'}
              </NavBtn>
              {!menuCfg?.ocultar_perfil && (
                <Avatar to="/perfil" title="Mi perfil" $bg={primary}>
                  {user.avatar_url
                    ? <img src={`/backend/${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : iniciales}
                </Avatar>
              )}
              {mostrarSelectorIdioma && (
                <LangGroup>
                  {idiomasHabilitados.map(lang => (
                    <LangBtn key={lang} $active={idioma === lang} $accent={primary} onClick={() => setIdioma(lang)}>
                      {lang.toUpperCase()}
                    </LangBtn>
                  ))}
                </LangGroup>
              )}
            </>
            )
          ) : (
            // ============== USUARIO NO LOGUEADO ==============
            <>
              {c.login_icono === true && (lupa || c.boton_login !== false) && (
                <GrupoIconos $color={navColor}>
                  {lupa}
                  {lupa && c.boton_login !== false && <span className="sep" aria-hidden="true" />}
                  {c.boton_login !== false && (
                    <IconoBoton
                      type="button"
                      $color={navColor}
                      $acento={c.color_boton_registro || secondary}
                      onClick={() => handleAction('login')}
                      aria-label={tl(c.texto_boton_login, 'Iniciar sesión')}
                      data-tip={tl(c.texto_boton_login, 'Iniciar sesión')}
                    >
                      <IcoPersona />
                    </IconoBoton>
                  )}
                </GrupoIconos>
              )}
              {(c.boton_login !== false) && c.login_icono !== true && (
                <NavBtn
                  $bg="transparent"
                  $color={navColor}
                  $border={c.borde_boton_login || '1px solid transparent'}
                  $borderLeft={c.borde_login_izq}
                  $borderRight={c.borde_login_der}
                  $padding={c.padding_login || c.padding_botones}
                  $radius={c.radio_botones}
                  $weight={c.peso_login}
                  $size={c.tamano_login || c.tamano_botones}
                  onClick={() => handleAction('login')}
                >
                  {tl(c.texto_boton_login, 'Iniciar sesión')}
                </NavBtn>
              )}
              {c.login_icono === true && c.mostrar_olvido !== false && (
                <BotonOlvido
                  type="button"
                  $bg={c.color_boton_registro || secondary}
                  $color={c.color_texto_boton_registro || '#fff'}
                  $size={c.tamano_registro || c.tamano_botones}
                  onClick={() => { if (accesoEnPagina) { navigate('/login?vista=olvido'); return; } setVistaAcceso('olvido'); setAccesoAbierto(true); }}
                  aria-label={tl(c.texto_boton_olvido, 'Olvidé mi contraseña')}
                  title={tl(c.texto_boton_olvido, 'Olvidé mi contraseña')}
                >
                  <span className="pozo"><IcoCandado /></span>
                  <span className="texto">
                    {c.subtexto_boton_olvido !== '' && <small>{tl(c.subtexto_boton_olvido, '¿No puedes entrar?')}</small>}
                    {tl(c.texto_boton_olvido, 'Olvidé mi contraseña')}
                  </span>
                </BotonOlvido>
              )}
              {(c.boton_registro !== false) && c.login_icono !== true && (
                <NavBtn
                  $bg={c.color_boton_registro || secondary}
                  $color={c.color_texto_boton_registro || '#fff'}
                  $border={c.borde_boton_registro}
                  $borderLeft={c.borde_registro_izq}
                  $borderRight={c.borde_registro_der}
                  $padding={c.padding_registro || c.padding_botones}
                  $radius={c.radio_botones}
                  $weight={c.peso_registro}
                  $size={c.tamano_registro || c.tamano_botones}
                  onClick={() => handleAction('registro')}
                >
                  {tl(c.texto_boton_registro, 'Registrarse')}
                </NavBtn>
              )}
            </>
          )}
          {selectorPais}
        </NavArea>
        );
        // Con barra activa: columna derecha = ítems de la barra arriba, menú abajo.
        if (mostrarTopbar) {
          return (
            <RightCol
              style={{ order: menuOrder }}
              $align={c.topbar_alineacion || 'derecha'}
              $gap={c.topbar_gap}
              $color={c.topbar_color}
              $size={c.topbar_font_size}
            >
              <RightColTop $ocultarMovil={c.topbar_ocultar_movil === true}>
                {renderTopItems()}
              </RightColTop>
              {nav}
            </RightCol>
          );
        }
        return React.cloneElement(nav, { style: { order: menuOrder } });
        })()}
      </Inner>
      )}
      {c.login_icono === true && (
        <AccesoIconoModal
          abierto={accesoAbierto}
          onCerrar={() => setAccesoAbierto(false)}
          vistaInicial={vistaAcceso}
          onRegistroExterno={openRegister ? () => handleAction('registro') : undefined}
          acento={c.color_boton_registro || secondary}
          textoAcento={c.color_texto_boton_registro || '#fff'}
          fondo={c.color_fondo_scroll || '#060b14'}
          fuente={c.fuente_menu}
          destino={c.login_destino}
        />
      )}
      {lupa && (
        <BuscadorCursos
          abierto={buscadorAbierto}
          onCerrar={() => setBuscadorAbierto(false)}
          proyecto={proyecto}
          acento={c.color_boton_registro || secondary}
          textoAcento={c.color_texto_boton_registro || '#fff'}
          fondo={c.color_fondo_scroll || '#0d1624'}
          fuente={c.fuente_menu}
        />
      )}
    </Wrapper>
  );
};

export default HeaderSection;
